import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  ButtonComponent,
  EmptyPlaceholderComponent,
  ScoreChipComponent,
  TagComponent,
  TagVariant,
  resolveScoreChipLevel,
} from '@catch/general-ui';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowUpRightFromSquare,
  faPaperPlane,
  faPen,
  faSearch,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { map } from 'rxjs';
import { viewLoader } from '../../../../../../assets/animations/animations';
import { PlatformJobBookmarkButtonComponent } from '../../../../components/job-bookmark-button/platform-job-bookmark-button.component';
import { PlatformCompanyLogoComponent } from '../../../../components/company-logo/platform-company-logo.component';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';
import { PlatformApplyWithProfileService } from '../../../../services/platform-apply-with-profile.service';

@Component({
  selector: 'app-platform-job-detail-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    EmptyPlaceholderComponent,
    RouterLink,
    ScoreChipComponent,
    TagComponent,
    FontAwesomeModule,
    TranslocoPipe,
    PlatformCompanyLogoComponent,
    PlatformJobBookmarkButtonComponent,
  ],
  templateUrl: './platform-job-detail-view.component.html',
  styleUrl: './platform-job-detail-view.component.scss',
  animations: [viewLoader],
})
export class PlatformJobDetailViewComponent {
  private readonly route = inject(ActivatedRoute);
  readonly jobs = inject(PlatformJobsService);
  private readonly applyWithProfile = inject(PlatformApplyWithProfileService);

  readonly TagVariant = TagVariant;
  readonly icons = {
    apply: faPaperPlane,
    pen: faPen,
    external: faArrowUpRightFromSquare,
    search: faSearch,
  };

  private readonly jobId = toSignal(
    this.route.paramMap.pipe(map((params) => Number(params.get('jobId')))),
    { initialValue: Number(this.route.snapshot.paramMap.get('jobId')) }
  );

  readonly job = computed(() => {
    const id = this.jobId();
    if (!Number.isFinite(id)) {
      return undefined;
    }
    return this.jobs.boardJobById(id);
  });

  readonly scoreLabelKey = computed(() => {
    const score = this.job()?.score;
    if (score == null) {
      return '';
    }
    return `jobboard.platform.recommendations.score.${resolveScoreChipLevel(score)}`;
  });

  readonly workingModelLabelKey = computed(() => {
    const model = this.job()?.workingModel;
    return model ? `jobboard.platform.profile.working-model.${model}` : '';
  });

  readonly employmentTypeKey = computed(() => {
    const type = this.job()?.employmentType;
    return type ? `ct-library.constants.employment-type.${type}` : '';
  });

  readonly workingTimeKey = computed(() => {
    const time = this.job()?.workingTime;
    return time ? `ct-library.constants.working-hours.${time}` : '';
  });

  readonly applyLabelKey = computed(() =>
    this.job()?.invited
      ? 'jobboard.platform.recommendations.apply-invited-cta'
      : 'jobboard.platform.recommendations.apply-cta'
  );

  constructor() {
    const id = Number(this.route.snapshot.paramMap.get('jobId'));
    if (Number.isFinite(id)) {
      this.jobs.loadBoardJob(id);
    }
  }

  /**
   * Opens the apply-with-profile modal for this job.
   */
  apply(): void {
    const job = this.job();
    if (!job) {
      return;
    }
    this.applyWithProfile.open(this.jobs.applyContextFromBoardJob(job));
  }

}
